import { useEffect, useState } from "react";
import { useAdminStore } from "@/lib/stores/admin-store";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AlertCircle, Search, Download, CreditCard, Sliders, Wallet } from "lucide-react";
import { Button } from "@/components/ui/Button";

// Extended interfaces for the data we need
interface ExtendedUser {
  id: string;
  email: string;
  role: string;
  created_at: string;
  creator_profile?: {
    id: string;
    business_name?: string;
    stripe_account_id?: string;
  } | null;
}

interface ExtendedBooking {
  id: string;
  user_id: string;
  experience_id: string;
  experience_name?: string;
  total_amount?: number;
  payment_status?: string;
  created_at: string;
}

/**
 * Admin Payments Management Page
 * With Stripe Connect integration for viewing creator payouts
 */
export function PaymentsPage() {
  const { 
    isInitialized, 
    error, 
    users,
    bookings,
    fetchUsers,
    fetchBookings
  } = useAdminStore();
  
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<'all' | 'month' | 'week' | 'today'>('month');
  const [paymentType, setPaymentType] = useState<'all' | 'creators' | 'platform'>('all');

  // Refresh data when component mounts
  useEffect(() => {
    if (isInitialized) {
      Promise.all([
        fetchUsers().catch(err => console.error("[AdminPayments] Error refreshing users:", err)),
        fetchBookings().catch(err => console.error("[AdminPayments] Error refreshing bookings:", err))
      ]);
    }
  }, [isInitialized, fetchUsers, fetchBookings]);

  // Full page loading state
  if (!isInitialized) {
    return <LoadingSpinner />;
  }
  
  // Cast to our extended interfaces
  const typedUsers = users as ExtendedUser[];
  const typedBookings = bookings as ExtendedBooking[];
  
  // Get creators with Stripe accounts
  const creators = typedUsers.filter(u => u.role === 'creator');
  const creatorsWithStripe = creators.filter(c => c.creator_profile?.stripe_account_id);
  const creatorsWithoutStripe = creators.filter(c => !c.creator_profile?.stripe_account_id);
  
  // Filter bookings by date range
  const now = new Date();
  const filteredBookings = typedBookings.filter(booking => {
    const bookingDate = new Date(booking.created_at);
    
    switch(dateRange) {
      case 'today':
        return bookingDate.toDateString() === now.toDateString();
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        return bookingDate >= weekAgo;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(now.getMonth() - 1);
        return bookingDate >= monthAgo;
      default:
        return true;
    }
  });
  
  // Calculate payment totals
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
  const platformFee = totalRevenue * 0.15; // Assuming 15% platform fee
  const creatorPayouts = totalRevenue - platformFee;
  
  // Search filter
  const searchResults = creators.filter(creator => {
    if (!search) return true;
    
    const searchText = search.toLowerCase();
    return (
      creator.email.toLowerCase().includes(searchText) ||
      (creator.creator_profile?.business_name || '').toLowerCase().includes(searchText)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Errors banner */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-400 mr-3" />
            <div>
              <p className="text-sm text-red-300 font-medium">
                Error loading payment data
              </p>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-earth-700 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-full p-3 bg-green-500/20">
                <CreditCard className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-sand-400 truncate">Total Revenue</dt>
                  <dd className="text-lg font-medium text-sand-100">${totalRevenue.toFixed(2)}</dd>
                  <dd className="text-xs font-medium text-sand-400">
                    {dateRange === 'today' ? 'Today' : 
                     dateRange === 'week' ? 'Last 7 days' : 
                     dateRange === 'month' ? 'Last 30 days' : 'All time'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-earth-700 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-full p-3 bg-purple-500/20">
                <Sliders className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-sand-400 truncate">Platform Revenue</dt>
                  <dd className="text-lg font-medium text-sand-100">${platformFee.toFixed(2)}</dd>
                  <dd className="text-xs font-medium text-sand-400">
                    15% platform fee
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-earth-700 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-full p-3 bg-yellow-500/20">
                <Wallet className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-sand-400 truncate">Creator Payouts</dt>
                  <dd className="text-lg font-medium text-sand-100">${creatorPayouts.toFixed(2)}</dd>
                  <dd className="text-xs font-medium text-sand-400">
                    {creatorsWithStripe.length} of {creators.length} creators connected
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={dateRange === 'all' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setDateRange('all')}
          className={dateRange === 'all' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
        >
          All Time
        </Button>
        <Button
          variant={dateRange === 'month' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setDateRange('month')}
          className={dateRange === 'month' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
        >
          Last 30 Days
        </Button>
        <Button
          variant={dateRange === 'week' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setDateRange('week')}
          className={dateRange === 'week' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
        >
          Last 7 Days
        </Button>
        <Button
          variant={dateRange === 'today' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setDateRange('today')}
          className={dateRange === 'today' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
        >
          Today
        </Button>
      </div>

      {/* Creator Payments Section */}
      <div className="bg-earth-700 shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="text-lg leading-6 font-display text-sand-100">Creator Payments</h3>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-sand-400" />
              </div>
              <input
                type="text"
                placeholder="Search creators..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-earth-700 border border-earth-600 text-sand-200 placeholder-sand-500 block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-sand-400"
              />
            </div>
            
            <Button variant="outline" size="sm" className="bg-earth-600 text-sand-300">
              <Download className="h-4 w-4 mr-1" /> Export Payments
            </Button>
          </div>
        </div>
        
        {/* Creator Payment Table */}
        <div className="border-t border-earth-600">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-earth-600">
              <thead className="bg-earth-800">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Creator
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Stripe Connected
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                    Payouts
                  </th>
                </tr>
              </thead>
              <tbody className="bg-earth-700 divide-y divide-earth-600">
                {searchResults.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sand-400">
                      No creators found matching your search
                    </td>
                  </tr>
                ) : (
                  searchResults.map((creator) => {
                    // Calculate bookings and revenue for this creator
                    const creatorBookings = filteredBookings.filter(booking => {
                      // Since we don't have creator_id in bookings, this is a placeholder
                      // In a real system, you would properly join bookings to creators through experiences
                      return booking.experience_id && booking.experience_id.startsWith(creator.id.substring(0, 5));
                    });
                    
                    const revenue = creatorBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
                    const payout = revenue * 0.85; // 85% to creator
                    
                    return (
                      <tr key={creator.id} className="hover:bg-earth-600/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-sand-200">{creator.email}</div>
                          <div className="text-xs text-sand-400">{creator.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-sand-300">
                            {creator.creator_profile?.business_name || "Unnamed Business"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {creator.creator_profile?.stripe_account_id ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-300">
                              Connected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-900/30 text-yellow-300">
                              Not Connected
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-sand-300">
                          {creatorBookings.length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-sand-300">
                          ${revenue.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-sand-300">
                          ${payout.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
} 